'use client';

/**
 * @fileoverview Reusable home/apps link used in topbar, side menu, chat, editor.
 * @purpose Links to app home with the shared 3×3 grid and a random hover/focus micro-animation.
 * @ownership Core package
 */

import Link from 'next/link';
import { useState } from 'react';

import {
  HomeGridIcon,
  type HomeGridIconAnimation,
  pickRandomHomeGridAnimation,
} from './Icon/HomeGridIcon';

export type AppsHomeLinkProps = {
  /** When true, show label next to icon (e.g. "Apps" or "Home"). Default false for icon-only. */
  showLabel?: boolean;
  /** Label text when showLabel is true. Default "Apps". */
  label?: string;
  /** Optional className for the link. */
  className?: string;
  /** Optional inline style for the link. */
  style?: React.CSSProperties;
  /** Optional click handler (e.g. close drawer). */
  onClick?: () => void;
  /** data-testid for the link. Default "apps-button" when showLabel, "topbar-home" when icon-only. */
  'data-testid'?: string;
  /** aria-label. Default "Go to app home". */
  'aria-label'?: string;
  /** Icon size class. Default "h-5 w-5". */
  iconClassName?: string;
  /** When true, use primary styling (e.g. drawer when no extra content). */
  primary?: boolean;
  /** When false, keep the grid static (no random hover animation). */
  animateIcon?: boolean;
};

const DEFAULT_ARIA_LABEL = 'Go to app home';

/**
 * Link to "/" with apps-as-cards icon; optionally shows a label.
 * Use icon-only in topbar; use icon + label in side menu, chat sidebar, editor.
 */
export function AppsHomeLink({
  showLabel = false,
  label = 'Apps',
  className,
  style,
  onClick,
  'data-testid': dataTestId,
  'aria-label': ariaLabel = DEFAULT_ARIA_LABEL,
  iconClassName = 'h-5 w-5 shrink-0',
  primary = false,
  animateIcon = true,
}: AppsHomeLinkProps) {
  const [iconAnim, setIconAnim] = useState<HomeGridIconAnimation | null>(null);

  const activateIcon = () => {
    if (animateIcon) {
      setIconAnim(pickRandomHomeGridAnimation());
    }
  };
  const deactivateIcon = () => setIconAnim(null);

  const baseClass = 'flex items-center gap-2 rounded-md transition-colors hover:opacity-90';
  const finalClassName = className ?? (showLabel ? `${baseClass} px-4 py-3 font-semibold mb-6 w-full` : `${baseClass} p-2 justify-center shrink-0`);
  const linkStyle: React.CSSProperties = primary
    ? { color: 'var(--primary-foreground)', backgroundColor: 'var(--primary-default)', border: 'none', ...style }
    : { color: 'var(--foreground-default)', backgroundColor: 'transparent', ...style };
  const iconStyle = primary ? { color: 'var(--primary-foreground)' } : undefined;

  return (
    <Link
      href="/"
      className={finalClassName}
      style={linkStyle}
      onClick={onClick}
      onMouseEnter={activateIcon}
      onMouseLeave={deactivateIcon}
      onFocus={activateIcon}
      onBlur={deactivateIcon}
      data-testid={dataTestId ?? (showLabel ? 'apps-button' : 'topbar-home')}
      aria-label={ariaLabel}
    >
      <HomeGridIcon
        className={iconClassName}
        style={iconStyle}
        activeAnimation={iconAnim}
        chromatic={animateIcon && iconAnim !== null}
        idleFill={primary ? 'var(--primary-foreground)' : '#ffffff'}
        wrapperClassName="shrink-0"
      />
      {showLabel && <span style={primary ? { color: 'var(--primary-foreground)' } : undefined}>{label}</span>}
    </Link>
  );
}
